import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Spinner } from "reactstrap";
import moment from "moment";
import api from "../../../services/api";
import { listarServicosConector } from "../../../services/integracaoTransportadoraApi";
import { downloadBlob } from "../../../utils/downloadBlob";
import { getQuoteStatusLabel } from "../ListagemCotacoes/statusOptions";
import MapaEntregaApp from "./MapaEntregaApp";

// Aba "Etiqueta" do detalhe da NF (PO1-3378, épico PO1-2570). Lista os envios
// já gerados (NotaFiscalEnvio) com PDF/ZPL, cancelar (onde o conector permite)
// e "Solicitar coleta" (Correios, por NF); e o formulário "Gerar etiqueta":
// transportadora/serviço pré-preenchidos pela cotação ligada pelo BR, ou a
// lista cotada agora com preço; volumes editáveis (caixas da cotação ou
// dimensões da NF); checkbox de cobrança no Melhor Envio; pendências de
// cadastro listadas em vez de chamar a API.
//
// API (backend_hub, JWT): GET/POST /notas-fiscais/consulta/:id/etiqueta,
// GET .../etiqueta/:idEnvio/arquivo?formato=pdf|zpl (blob), POST
// .../etiqueta/:idEnvio/cancelar, POST .../etiqueta/coleta.
//
// Apps de entrega (sprint 636: Lalamove, Uber Direct; vínculo `semEtiqueta`):
// "Solicitar entrega" no lugar de "Gerar etiqueta", serviço só pelas propostas
// cotadas (o valor escolhido vai como `valorCotado`), "não usa etiqueta" na
// linha do envio e "Acompanhar entrega" (mapa próprio + página do app em
// iframe, com o link para abrir em nova aba). GET .../etiqueta/:idEnvio/posicao.

const fmtCurrency = (v) =>
  v == null ? "-" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (v, withTime) => (v ? moment(v).format(withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY") : "-");

export const STATUS_ENVIO = {
  criando: { label: "Criando", tone: "neutral" },
  pendente: { label: "Etiqueta em processamento", tone: "warning" },
  emitida: { label: "Emitida", tone: "success" },
  cancelada: { label: "Cancelada", tone: "danger" },
  falha: { label: "Falha", tone: "danger" },
};

const CONECTOR_LABELS = {
  "correios-cws": "Correios",
  "melhor-envio": "Melhor Envio",
  mandae: "Mandaê",
  loggi: "Loggi",
  lalamove: "Lalamove",
  "uber-direct": "Uber Direct",
  "jt-express": "J&T Express",
  track3r: "Buslog (TRACK3R)",
  "total-express": "Total Express",
};
const conectorLabel = (k) => CONECTOR_LABELS[k] || k || "-";

// Conectores de app de entrega: pedido sem etiqueta para imprimir, com
// "Solicitar entrega" e "Acompanhar entrega".
const CONECTORES_APP = ["lalamove", "uber-direct"];
const ehApp = (k) => CONECTORES_APP.includes(k);
export const ehEnvioDeApp = (envio, transp) =>
  !!((envio && ehApp(envio.conectorKey)) || (transp && transp.semEtiqueta && ehApp(transp.conectorKey)));
// Sprint 635 (decisão 6A): transportadora sem etiqueta que NÃO é app (Buslog/
// TRACK3R, Total Express): "Enviar à transportadora", e no envio "Etiqueta
// emitida pela transportadora" no lugar dos botões de formato.
const CONECTORES_SEM_ETIQUETA = ["track3r", "total-express"];
export const ehEnvioSemEtiqueta = (envio, transp) =>
  !ehEnvioDeApp(envio, transp) &&
  !!((transp && transp.semEtiqueta) || (envio && CONECTORES_SEM_ETIQUETA.includes(envio.conectorKey)));

// Último status lido do app (rastreio de 5 min), em português.
export const STATUS_APP = {
  ASSIGNING_DRIVER: "Procurando entregador",
  ON_GOING: "Entregador a caminho da coleta",
  PICKED_UP: "Entregador com a carga",
  COMPLETED: "Entregue",
  CANCELED: "Cancelado no app",
  REJECTED: "Recusado no app",
  EXPIRED: "Expirou sem entregador",
  pending: "Procurando entregador",
  pickup: "Entregador a caminho da coleta",
  pickup_complete: "Entregador com a carga",
  dropoff: "A caminho da entrega",
  delivered: "Entregue",
  canceled: "Cancelado no app",
  returned: "Devolvido ao remetente",
};

const volumeVazio = () => ({ quantidade: 1, pesoKg: "", altura: "", largura: "", comprimento: "" });
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

// Opções do campo Serviço (nunca texto livre). Com proposta cotada, a lista
// vem dela (nome, preço, prazo). Sem proposta, depende do conector: Correios
// lista os serviços do contrato; Mandaê tem os dois serviços fixos; Loggi usa
// o serviço do contrato (o conector manda o externalServiceId do vínculo);
// Melhor Envio só gera com serviço cotado (o id vem da cotação).
export const SERVICO_PADRAO = "__padrao__";
export function opcoesServico(transp, propostasDaTransp, contratados) {
  if (!transp) return [];
  // App de entrega: só o que foi cotado agora (veículo na Lalamove; o Uber
  // escolhe o veículo e devolve uma proposta sem serviço).
  if (transp.semEtiqueta) {
    return (propostasDaTransp || []).map((p) => ({
      value: p.servico ? String(p.servico) : SERVICO_PADRAO,
      label: `${p.transportadora} — ${fmtCurrency(p.custoFrete)}`,
      custoFrete: p.custoFrete,
    }));
  }
  if (propostasDaTransp && propostasDaTransp.length) {
    return propostasDaTransp
      .filter((p) => p.servico)
      .map((p) => ({
        value: String(p.servico),
        label: `${p.transportadora} — ${fmtCurrency(p.custoFrete)}${p.prazo != null ? ` · ${p.prazo}d` : ""}`,
      }));
  }
  switch (transp.conectorKey) {
    case "correios-cws":
      return contratados && contratados.length
        ? contratados.map((c) => ({ value: String(c.codigo), label: c.descricao ? `${c.descricao} (${c.codigo})` : String(c.codigo) }))
        : [{ value: SERVICO_PADRAO, label: "Serviço padrão do contrato" }];
    case "mandae":
      return [
        { value: "Econômico", label: "Econômico" },
        { value: "Rápido", label: "Rápido" },
      ];
    case "melhor-envio":
      return [];
    default:
      return [{ value: SERVICO_PADRAO, label: "Serviço do contrato" }];
  }
}

// Nome do serviço de um envio ("Correios SEDEX") a partir das propostas da
// mesma transportadora; sem proposta correspondente, fica o código cru.
export function rotuloServico(envio, propostas) {
  if (!envio || !envio.servico) return { nome: "-", codigo: null };
  const p = (propostas || []).find(
    (x) => String(x.idTransportadora) === String(envio.idTransportadora) && String(x.servico) === String(envio.servico)
  );
  return p ? { nome: p.transportadora, codigo: String(envio.servico) } : { nome: String(envio.servico), codigo: null };
}

// Volumes sugeridos (API) -> linhas do formulário (strings para os inputs).
export function linhasDeVolumes(volumes) {
  const lista = Array.isArray(volumes) && volumes.length ? volumes : [volumeVazio()];
  return lista.map((v) => ({
    quantidade: v.quantidade != null ? String(v.quantidade) : "1",
    pesoKg: v.pesoKg != null ? String(v.pesoKg) : "",
    altura: v.altura != null ? String(v.altura) : "",
    largura: v.largura != null ? String(v.largura) : "",
    comprimento: v.comprimento != null ? String(v.comprimento) : "",
  }));
}

const Section = ({ eyebrow, title, children, className = "" }) => (
  <section className={`nfe-detail-section ${className}`}>
    <div className="nfe-detail-section__header">
      {eyebrow && <span>{eyebrow}</span>}
      <h4>{title}</h4>
    </div>
    {children}
  </section>
);

const EtiquetaTab = ({ notaFiscalId, embarcadorId, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [dados, setDados] = useState(null);
  const [aviso, setAviso] = useState(null); // { tone, texto }

  const [idTransportadora, setIdTransportadora] = useState("");
  const [servico, setServico] = useState("");
  const [volumes, setVolumes] = useState([volumeVazio()]);
  const [confirmarCobranca, setConfirmarCobranca] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [baixando, setBaixando] = useState(null); // `${idEnvio}:${formato}`
  const [cancelando, setCancelando] = useState(null);
  const [coletaData, setColetaData] = useState("");
  const [coletando, setColetando] = useState(false);
  const [contratados, setContratados] = useState({}); // idTransportadora -> [{codigo, descricao}]
  const [acompanhando, setAcompanhando] = useState(null); // envio do app no modal

  const params = useMemo(() => ({ embarcadorId: embarcadorId || undefined }), [embarcadorId]);
  const base = `/notas-fiscais/consulta/${notaFiscalId}/etiqueta`;

  const carregar = useCallback(async () => {
    if (!notaFiscalId) return;
    setLoading(true);
    setErro("");
    try {
      const { data } = await api.get(base, { params });
      setDados(data);
      // pré-preenche pela sugestão (cotação ligada pelo BR ou 1ª proposta)
      if (data && data.sugestao) {
        setIdTransportadora(String(data.sugestao.idTransportadora || ""));
        setServico(data.sugestao.servico || "");
      }
      setVolumes(linhasDeVolumes(data && data.volumesSugeridos));
    } catch (e) {
      setErro((e.response && e.response.data && e.response.data.error) || "Não foi possível carregar a aba Etiqueta.");
    } finally {
      setLoading(false);
    }
  }, [base, notaFiscalId, params]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const transportadoras = (dados && dados.transportadoras) || [];
  const propostas = (dados && dados.propostas) || [];
  const envios = (dados && dados.envios) || [];
  const cotacao = dados && dados.cotacao;
  const transpSel = transportadoras.find((t) => String(t.idTransportadora) === String(idTransportadora)) || null;
  const propostasDaTransp = propostas.filter((p) => String(p.idTransportadora) === String(idTransportadora));
  const pendencias = (transpSel && transpSel.pendencias) || [];
  const exigeCobranca = !!(transpSel && transpSel.exigeConfirmacaoCobranca);
  // `semEtiqueta` = app de entrega (Solicitar entrega); `envioTransp` = transportadora
  // sem etiqueta para imprimir (Enviar à transportadora, sprint 635).
  const envioTransp = !!(transpSel && transpSel.semEtiqueta && !ehApp(transpSel.conectorKey));
  const semEtiqueta = !!(transpSel && transpSel.semEtiqueta && !envioTransp);
  const escritaDesligada = !!(dados && dados.escritaDesligada);
  const enviosAtivos = envios.filter((e) => ["emitida", "pendente"].includes(e.status));
  const enviosCorreiosEmitidos = envios.filter((e) => e.status === "emitida" && e.conectorKey === "correios-cws" && !e.numeroColeta);

  const precisaContratados = !!(transpSel && transpSel.conectorKey === "correios-cws" && !propostasDaTransp.length);
  useEffect(() => {
    if (!precisaContratados || contratados[idTransportadora]) return undefined;
    let vivo = true;
    listarServicosConector(idTransportadora, embarcadorId)
      .then((r) => { if (vivo) setContratados((prev) => ({ ...prev, [idTransportadora]: (r && r.servicos) || [] })); })
      .catch(() => { if (vivo) setContratados((prev) => ({ ...prev, [idTransportadora]: [] })); });
    return () => { vivo = false; };
  }, [precisaContratados, idTransportadora, embarcadorId, contratados]);

  const servicos = opcoesServico(transpSel, propostasDaTransp, contratados[idTransportadora]);
  const unicoServico = servicos.length === 1 ? servicos[0].value : null;
  useEffect(() => {
    if (!servico && unicoServico) setServico(unicoServico);
  }, [servico, unicoServico]);
  const servicoOk = !!servico && servicos.some((o) => o.value === servico);
  const opcaoSel = servicos.find((o) => o.value === servico);
  const valorCotado = semEtiqueta && opcaoSel && opcaoSel.custoFrete != null ? Number(opcaoSel.custoFrete) : null;

  const volumesValidos = volumes.every((v) => num(v.pesoKg) > 0 && (num(v.quantidade) || 1) >= 1);
  const podeGerar = !!transpSel && servicoOk && pendencias.length === 0 && volumesValidos && (!exigeCobranca || confirmarCobranca) && !gerando;

  const setVolume = (i, campo, valor) =>
    setVolumes((prev) => prev.map((v, idx) => (idx === i ? { ...v, [campo]: valor } : v)));

  const gerar = async () => {
    if (!podeGerar) return;
    setGerando(true);
    setAviso(null);
    try {
      const { data } = await api.post(
        base,
        {
          idTransportadora: Number(idTransportadora),
          servico: servico && servico !== SERVICO_PADRAO ? servico : null,
          volumes: volumes.map((v) => ({
            quantidade: num(v.quantidade) || 1,
            pesoKg: num(v.pesoKg),
            altura: num(v.altura),
            largura: num(v.largura),
            comprimento: num(v.comprimento),
          })),
          idCotacao: dados && dados.sugestao && dados.sugestao.idCotacao ? dados.sugestao.idCotacao : null,
          confirmarCobranca: exigeCobranca ? confirmarCobranca : false,
          ...(semEtiqueta ? { valorCotado } : {}),
        },
        { params }
      );
      const ok = data && data.status !== "falha";
      setAviso({
        tone: ok ? (data.dryRun ? "info" : "success") : "danger",
        texto: ok && semEtiqueta
          ? data.dryRun
            ? `Modo teste: o pedido foi simulado, nada foi enviado ao ${transpSel.fantasia}.`
            : `Entrega solicitada ao ${transpSel.fantasia}. Acompanhe pelo botão "Acompanhar entrega".`
          : ok && envioTransp
          ? data.dryRun
            ? `Modo teste: o envio foi simulado, nada foi enviado à ${transpSel.fantasia}.`
            : `Envio registrado na ${transpSel.fantasia}. A etiqueta é emitida pela transportadora.`
          : ok
          ? data.dryRun
            ? "Modo teste: a etiqueta foi simulada, nada foi enviado à transportadora."
            : data.status === "pendente"
              ? "Envio criado; a etiqueta está sendo processada pela transportadora. Tente baixar em instantes."
              : `Etiqueta emitida (${(data.envios || []).length} objeto(s)).`
          : `Falha: ${((data && data.erros) || []).join("; ") || "a transportadora recusou o envio"}`.replace(/\.?$/, "."),
      });
      await carregar();
      if (onChanged) onChanged();
    } catch (e) {
      const d = (e.response && e.response.data) || {};
      setAviso({
        tone: "danger",
        texto: d.pendencias && d.pendencias.length
          ? `Faltam dados para ${semEtiqueta ? "solicitar a entrega" : envioTransp ? "registrar o envio" : "gerar a etiqueta"}: ${d.pendencias.join("; ")}.`
          : d.error ||
            (semEtiqueta
              ? "Não foi possível solicitar a entrega."
              : envioTransp
              ? "Não foi possível registrar o envio na transportadora."
              : "Não foi possível gerar a etiqueta."),
      });
    } finally {
      setGerando(false);
    }
  };

  const baixar = async (envio, formato) => {
    const chave = `${envio.id}:${formato}`;
    setBaixando(chave);
    setAviso(null);
    try {
      const resp = await api.get(`${base}/${envio.id}/arquivo`, { params: { ...params, formato }, responseType: "blob" });
      if (resp.status === 202) {
        setAviso({ tone: "warning", texto: "Etiqueta em processamento na transportadora; tente em instantes." });
        return;
      }
      downloadBlob(resp.data, `etiqueta-nf-${notaFiscalId}-${envio.id}.${formato}`);
      // o código de rastreio do envio que nasceu sem ele chega junto com o arquivo
      if (envio.status === "pendente" || !envio.codigoRastreio) await carregar();
    } catch (e) {
      let msg = "Não foi possível baixar a etiqueta.";
      const d = e.response && e.response.data;
      if (d && typeof d.text === "function") {
        try { msg = JSON.parse(await d.text()).error || msg; } catch (_) { /* blob não-JSON */ }
      } else if (d && d.error) msg = d.error;
      setAviso({ tone: e.response && e.response.status === 202 ? "warning" : "danger", texto: msg });
    } finally {
      setBaixando(null);
    }
  };

  const cancelar = async (envio) => {
    setCancelando(envio.id);
    setAviso(null);
    try {
      await api.post(`${base}/${envio.id}/cancelar`, {}, { params });
      setAviso({ tone: "success", texto: `Envio ${envio.codigoRastreio || envio.id} cancelado na transportadora.` });
      await carregar();
      if (onChanged) onChanged();
    } catch (e) {
      setAviso({ tone: "danger", texto: (e.response && e.response.data && e.response.data.error) || "Não foi possível cancelar o envio." });
    } finally {
      setCancelando(null);
    }
  };

  const solicitarColeta = async () => {
    if (!coletaData) return;
    setColetando(true);
    setAviso(null);
    try {
      const { data } = await api.post(`${base}/coleta`, { dataAgendada: coletaData }, { params });
      const ok = data && data.status === "ok";
      setAviso({
        tone: ok ? "success" : "danger",
        texto: ok
          ? `Coleta ${data.numero || ""} solicitada aos Correios para ${moment(coletaData).format("DD/MM/YYYY")}.`
          : `Falha ao pedir a coleta: ${((data && data.erros) || []).join("; ") || "sem detalhe"}.`,
      });
      await carregar();
    } catch (e) {
      setAviso({ tone: "danger", texto: (e.response && e.response.data && e.response.data.error) || "Não foi possível pedir a coleta." });
    } finally {
      setColetando(false);
    }
  };

  if (loading && !dados) {
    return <div className="nfe-detail-state"><Spinner color="info" /> Carregando etiquetas...</div>;
  }
  if (erro) return <div className="nfe-detail-state nfe-detail-state--error">{erro}</div>;
  if (!dados) return null;

  return (
    <div className="nfe-detail-etiqueta" data-testid="etiqueta-tab">
      {aviso && (
        <div className={`nfe-detail-state${aviso.tone === "danger" ? " nfe-detail-state--error" : ""}`} data-testid="etiqueta-aviso">
          {aviso.texto}
        </div>
      )}
      {escritaDesligada && (
        <div className="nfe-detail-state">Integrações de escrita desligadas no servidor: toda geração sai em modo teste.</div>
      )}

      <Section eyebrow="Envios" title={`Etiquetas geradas (${envios.length})`}>
        {!envios.length ? (
          <div className="nfe-detail-empty">Nenhuma etiqueta gerada para esta NFe.</div>
        ) : (
          <div className="nfe-detail-table-wrap">
            <table className="nfe-detail-table hub-thead-accent">
              <thead>
                <tr>
                  <th>Transportadora</th>
                  <th>Serviço</th>
                  <th>Código de rastreio</th>
                  <th>Situação</th>
                  <th>Coleta</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {envios.map((envio) => {
                  const meta = STATUS_ENVIO[envio.status] || { label: envio.status, tone: "neutral" };
                  const transp = transportadoras.find((t) => Number(t.idTransportadora) === Number(envio.idTransportadora));
                  const app = ehEnvioDeApp(envio, transp);
                  const semEtiquetaTransp = ehEnvioSemEtiqueta(envio, transp);
                  const formatos = app || semEtiquetaTransp ? [] : envio.formatos && envio.formatos.length ? envio.formatos : ["pdf"];
                  const ativo = ["emitida", "pendente"].includes(envio.status);
                  return (
                    <tr key={envio.id} data-testid={`envio-${envio.id}`}>
                      <td>
                        <strong>{(transp && transp.fantasia) || conectorLabel(envio.conectorKey)}</strong>
                        <small>{fmtDate(envio.criadoEm, true)}</small>
                      </td>
                      <td>
                        {(() => {
                          const r = rotuloServico(envio, propostas);
                          return (
                            <>
                              <strong>{r.nome}</strong>
                              {r.codigo && <small className="nfe-detail-mono">{r.codigo}</small>}
                            </>
                          );
                        })()}
                      </td>
                      <td>
                        <strong className="nfe-detail-mono">{envio.codigoRastreio || "-"}</strong>
                        {envio.idEnvioTransportadora && <small className="nfe-detail-mono">{envio.idEnvioTransportadora}</small>}
                      </td>
                      <td>
                        <span className={`nfe-detail-pill nfe-detail-pill--${meta.tone}`}>{meta.label}</span>
                        {envio.dryRun && <span className="nfe-detail-pill nfe-detail-pill--info" style={{ marginLeft: 4 }}>Modo teste</span>}
                        {app && envio.statusTransportadora && (
                          <small style={{ display: "block" }} data-testid={`status-app-${envio.id}`}>
                            {STATUS_APP[envio.statusTransportadora] || envio.statusTransportadora}
                          </small>
                        )}
                        {envio.erro && <small style={{ display: "block", color: "var(--everlog-status-danger)" }}>{envio.erro}</small>}
                      </td>
                      <td>
                        {envio.numeroColeta ? (
                          <>
                            <strong>{envio.numeroColeta}</strong>
                            <small>{envio.statusColeta} · {fmtDate(envio.datColetaSolicitada)}</small>
                          </>
                        ) : "-"}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                          {app && (
                            <small data-testid={`sem-etiqueta-${envio.id}`}>
                              Este serviço não usa etiqueta
                              {envio.idEnvioTransportadora && !envio.dryRun ? ` · pedido ${envio.idEnvioTransportadora}` : ""}
                            </small>
                          )}
                          {semEtiquetaTransp && (
                            <small data-testid={`etiqueta-transportadora-${envio.id}`}>
                              Etiqueta emitida pela transportadora
                              {envio.idEnvioTransportadora && !envio.dryRun ? ` · encomenda ${envio.idEnvioTransportadora}` : ""}
                            </small>
                          )}
                          {app && ativo && envio.linkRastreio && !envio.dryRun && (
                            <Button
                              size="sm"
                              color="info"
                              onClick={() => setAcompanhando(envio)}
                              data-testid={`acompanhar-${envio.id}`}
                            >
                              Acompanhar entrega
                            </Button>
                          )}
                          {ativo && !envio.dryRun && formatos.map((f) => (
                            <Button
                              key={f}
                              size="sm"
                              color="default"
                              disabled={!!baixando}
                              onClick={() => baixar(envio, f)}
                              data-testid={`baixar-${envio.id}-${f}`}
                            >
                              {baixando === `${envio.id}:${f}` ? "..." : f.toUpperCase()}
                            </Button>
                          ))}
                          {ativo && transp && transp.cancelavel && (
                            <Button
                              size="sm"
                              color="danger"
                              outline
                              disabled={cancelando === envio.id}
                              onClick={() => cancelar(envio)}
                              data-testid={`cancelar-${envio.id}`}
                            >
                              {cancelando === envio.id ? "Cancelando..." : "Cancelar"}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {enviosCorreiosEmitidos.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 10, fontSize: 12 }} data-testid="coleta-correios">
            <span>Coleta dos Correios (por NF, depois da etiqueta):</span>
            <Input
              type="date"
              bsSize="sm"
              value={coletaData}
              min={moment().format("YYYY-MM-DD")}
              onChange={(e) => setColetaData(e.target.value)}
              style={{ width: 150 }}
              data-testid="coleta-correios-data"
            />
            <Button size="sm" color="info" disabled={!coletaData || coletando} onClick={solicitarColeta}>
              {coletando ? "Enviando..." : "Solicitar coleta"}
            </Button>
          </div>
        )}
      </Section>

      <Section
        eyebrow={semEtiqueta ? "Nova entrega" : envioTransp ? "Novo envio" : "Nova etiqueta"}
        title={semEtiqueta ? "Solicitar entrega" : envioTransp ? "Enviar à transportadora" : "Gerar etiqueta"}
      >
        {!transportadoras.length ? (
          <div className="nfe-detail-empty">
            Nenhuma transportadora integrada gera etiqueta para esta conta ainda. Para habilitar, fale com o suporte Everlog.
          </div>
        ) : (
          <div className="nfe-detail-fields nfe-detail-fields--three" style={{ alignItems: "end" }}>
            <div className="nfe-detail-field">
              <span>Transportadora {cotacao ? `· cotação ${cotacao.numero} (${getQuoteStatusLabel(cotacao.status)})` : "· sem cotação ligada pelo BR"}</span>
              <Input
                type="select"
                bsSize="sm"
                value={idTransportadora}
                onChange={(e) => { setIdTransportadora(e.target.value); setServico(""); setConfirmarCobranca(false); }}
                data-testid="etiqueta-transportadora"
              >
                <option value="">Selecione</option>
                {transportadoras.map((t) => (
                  <option key={t.idTransportadora} value={t.idTransportadora}>
                    {t.fantasia}{t.modoTeste ? " (modo teste)" : ""}
                  </option>
                ))}
              </Input>
            </div>
            <div className="nfe-detail-field">
              <span>Serviço</span>
              <Input
                type="select"
                bsSize="sm"
                value={servico}
                onChange={(e) => setServico(e.target.value)}
                disabled={!transpSel || !servicos.length}
                data-testid="etiqueta-servico"
              >
                <option value="">{transpSel && !servicos.length ? "Sem serviço cotado" : "Selecione"}</option>
                {servicos.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Input>
            </div>
            <div className="nfe-detail-field">
              <span>&nbsp;</span>
              <Button color="info" size="sm" disabled={!podeGerar} onClick={gerar} data-testid="etiqueta-gerar">
                {semEtiqueta
                  ? gerando ? "Solicitando..." : "Solicitar entrega"
                  : envioTransp
                  ? gerando ? "Enviando..." : "Enviar à transportadora"
                  : gerando ? "Gerando..." : "Gerar etiqueta"}
              </Button>
            </div>
          </div>
        )}

        {transpSel && (
          <>
            <div style={{ marginTop: 10, fontSize: 12 }}>
              <strong>Volumes</strong> {cotacao && cotacao.volumesSugeridos && cotacao.volumesSugeridos.length ? "(caixas da cotação)" : "(pela NF)"}
            </div>
            <div className="nfe-detail-table-wrap">
              <table className="nfe-detail-table" data-testid="etiqueta-volumes">
                <thead>
                  <tr><th>Qtd</th><th>Peso (kg)</th><th>Altura (cm)</th><th>Largura (cm)</th><th>Comprimento (cm)</th><th /></tr>
                </thead>
                <tbody>
                  {volumes.map((v, i) => (
                    <tr key={i}>
                      <td><Input bsSize="sm" type="number" min="1" value={v.quantidade} onChange={(e) => setVolume(i, "quantidade", e.target.value)} /></td>
                      <td><Input bsSize="sm" value={v.pesoKg} onChange={(e) => setVolume(i, "pesoKg", e.target.value)} placeholder="0,000" /></td>
                      <td><Input bsSize="sm" value={v.altura} onChange={(e) => setVolume(i, "altura", e.target.value)} /></td>
                      <td><Input bsSize="sm" value={v.largura} onChange={(e) => setVolume(i, "largura", e.target.value)} /></td>
                      <td><Input bsSize="sm" value={v.comprimento} onChange={(e) => setVolume(i, "comprimento", e.target.value)} /></td>
                      <td>
                        {volumes.length > 1 && (
                          <Button size="sm" color="link" onClick={() => setVolumes((prev) => prev.filter((_, idx) => idx !== i))} title="Remover volume">✕</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button size="sm" color="link" onClick={() => setVolumes((prev) => prev.concat([volumeVazio()]))}>+ volume</Button>

            {exigeCobranca && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginTop: 8 }} data-testid="etiqueta-cobranca">
                <input type="checkbox" checked={confirmarCobranca} onChange={(e) => setConfirmarCobranca(e.target.checked)} />
                {semEtiqueta
                  ? `Estou ciente de que o pedido no ${transpSel.fantasia} é cobrado na conta do embarcador no app.`
                  : `Estou ciente de que gerar a etiqueta no ${transpSel.fantasia} debita a carteira do embarcador.`}
              </label>
            )}
            {semEtiqueta && transpSel.pedidoReal === false && (
              <div style={{ fontSize: 12, color: "var(--everlog-card-muted)", marginTop: 6 }} data-testid="etiqueta-pedido-simulado">
                O pedido no {transpSel.fantasia} ainda está em modo teste na Everlog: ele será simulado, nada é enviado ao app.
              </div>
            )}
            {envioTransp && transpSel.pedidoReal === false && (
              <div style={{ fontSize: 12, color: "var(--everlog-card-muted)", marginTop: 6 }} data-testid="etiqueta-pedido-simulado">
                O envio para {transpSel.fantasia} ainda está em modo teste na Everlog: ele será simulado, nada é enviado à transportadora.
              </div>
            )}
            {envioTransp && (
              <div style={{ fontSize: 12, color: "var(--everlog-card-muted)", marginTop: 6 }} data-testid="etiqueta-emitida-transportadora">
                {transpSel.fantasia} emite a própria etiqueta: o Hub registra o envio e acompanha o rastreio.
              </div>
            )}

            {pendencias.length > 0 && (
              <div className="nfe-detail-state nfe-detail-state--error" data-testid="etiqueta-pendencias">
                Antes de gerar, complete o cadastro: {pendencias.join("; ")}.
              </div>
            )}
            {!servicos.length && (
              <div className="nfe-detail-state nfe-detail-state--error" data-testid="etiqueta-sem-servico">
                {semEtiqueta
                  ? `${transpSel.fantasia} não trouxe preço para esta NF agora. Abra a aba de novo para cotar.`
                  : `${transpSel.fantasia} não trouxe serviço para esta NF. Cote o frete (ou ligue a NF a uma cotação) para escolher o serviço.`}
              </div>
            )}
            {servico === SERVICO_PADRAO && !semEtiqueta && (
              <div style={{ fontSize: 12, color: "var(--everlog-card-muted)", marginTop: 6 }}>
                A etiqueta sai no serviço cadastrado no contrato com {transpSel.fantasia}.
              </div>
            )}
            {transpSel.coletaPorNota && (
              <div style={{ fontSize: 12, color: "var(--everlog-card-muted)", marginTop: 6 }}>
                Nos Correios a coleta é pedida aqui, por NF, depois da etiqueta.
              </div>
            )}
            {!transpSel.cancelavel && (
              <div style={{ fontSize: 12, color: "var(--everlog-card-muted)", marginTop: 6 }}>
                A integração com {transpSel.fantasia} não cancela envios: em caso de erro, cancele com a transportadora.
              </div>
            )}
          </>
        )}
        {enviosAtivos.length > 0 && !transpSel && (
          <div style={{ fontSize: 12, color: "var(--everlog-card-muted)", marginTop: 6 }}>
            Esta NF já tem {enviosAtivos.length} envio(s) ativo(s). Gere outro só se for um novo volume.
          </div>
        )}
      </Section>

      {/* Mesmo padrão do detalhe da NF: `modal-align-top` + `nfe-detail-modal` pintam o
          fundo e o texto pelos tokens do tema (sem eles o título saía branco no fundo branco). */}
      <Modal
        isOpen={!!acompanhando}
        toggle={() => setAcompanhando(null)}
        size="lg"
        modalClassName="modal-align-top"
        contentClassName="nfe-detail-modal"
        data-testid="acompanhar-modal"
      >
        {acompanhando && (
          <div className="modal-body" data-testid="acompanhar-entrega">
            <div className="nfe-detail-header" style={{ padding: 0, border: 0, marginBottom: 10 }}>
              <div>
                <span>Acompanhar entrega</span>
                <h3 data-testid="acompanhar-titulo">{conectorLabel(acompanhando.conectorKey)} · pedido {acompanhando.idEnvioTransportadora || "-"}</h3>
              </div>
              <div className="nfe-detail-header__actions">
                <button type="button" onClick={() => setAcompanhando(null)} title="Fechar" aria-label="Fechar">
                  ×
                </button>
              </div>
            </div>
            <MapaEntregaApp
              notaFiscalId={notaFiscalId}
              idEnvio={acompanhando.id}
              params={params}
              ativo={!!acompanhando}
            />
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              Página de acompanhamento do app.{" "}
              <a href={acompanhando.linkRastreio} target="_blank" rel="noopener noreferrer" data-testid="acompanhar-nova-aba">
                Abrir em nova aba
              </a>
              {" "}(se a página não aparecer abaixo, o app não permite exibi-la aqui).
            </div>
            <iframe
              title="Acompanhamento do app de entrega"
              src={acompanhando.linkRastreio}
              style={{ width: "100%", height: 420, border: "1px solid var(--everlog-card-border)", borderRadius: 8 }}
              referrerPolicy="no-referrer"
              data-testid="acompanhar-iframe"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EtiquetaTab;
